import * as stylex from "@stylexjs/stylex";
import { CalendarDays } from "lucide-react";
import { useState } from "react";
import { autoGrouping } from "@aco/core";
import type { Grouping } from "@aco/core";
import { ChipPanel, PanelColumn, PanelListSection, ChipPanelSection, PanelSegments } from "@/components/ReportingPanel";
import DatePicker from "@/components/ui/DatePicker";
import "@/design/tokens.stylex";
import { colors, space, text } from "@/design/tokens.stylex";
import type { ChangeDisplay, DateChange } from "@/lib/reporting-apply";
import { clampSelectableReportingDay, getDatePreset, latestSelectableReportingDay } from "@/lib/utils";
import { relativeReportingPresets } from "@/lib/workspace-default";

// One Date chip for the report's time settings: it reads "Last 7 days · Daily"
// and opens one column with the default ranges, a custom range, and (on
// Overview) the grouping and comparison. Editing a date makes the range custom
// (no default is checked); picking a default fills the dates again. Edits wait
// for Apply, then land in one step.

export type { ChangeDisplay } from "@/lib/reporting-apply";
const PRESETS = Object.entries(relativeReportingPresets).map(([value, label]) => ({
  value,
  label
}));
const GROUPINGS: {
  value: Grouping;
  label: string;
}[] = [{
  value: "day",
  label: "Daily"
}, {
  value: "week",
  label: "Weekly"
}, {
  value: "month",
  label: "Monthly"
}];
const CHANGES: {
  value: ChangeDisplay;
  label: string;
  summary?: string;
}[] = [{
  value: "off",
  label: "Off"
}, {
  value: "percent",
  label: "%",
  summary: "Compare %"
}, {
  value: "absolute",
  label: "Number",
  summary: "Compare number"
}];
const shortDate = (value: string, withYear: boolean) => new Date(`${value}T12:00:00`).toLocaleDateString("en-GB", {
  day: "numeric",
  month: "short",
  ...(withYear ? {
    year: "numeric"
  } : {})
});

/** "1 Jun – 7 Jun 2026", or one day when the range is a single day. */
function formatDateRange({
  start,
  end
}: {
  start: string;
  end: string;
}) {
  if (start === end) return shortDate(start, true);
  return `${shortDate(start, start.slice(0, 4) !== end.slice(0, 4))} – ${shortDate(end, true)}`;
}
interface DateDraft {
  preset: string;
  start: string;
  end: string;
  grouping: Grouping;
  /** Grouping was chosen here, so it outlasts a new range. */
  groupingChosen: boolean;
  changeDisplay: ChangeDisplay;
}
export default function ReportingDateChip({
  dateRange,
  preset,
  grouping,
  changeDisplay,
  showGrouping,
  showChange,
  onApply
}: {
  dateRange: {
    start: string;
    end: string;
  };
  preset: string;
  grouping: Grouping;
  changeDisplay: ChangeDisplay;
  showGrouping: boolean;
  showChange: boolean;
  onApply: (change: DateChange) => void;
}) {
  const [open, setOpen] = useState(false);
  const applied: DateDraft = {
    preset,
    start: dateRange.start,
    end: dateRange.end,
    grouping,
    groupingChosen: false,
    changeDisplay
  };
  const [draft, setDraft] = useState(applied);
  // Reporting is complete-days-only (issue 34): the calendars stop at yesterday.
  const maxSelectableDay = latestSelectableReportingDay();
  const summary = [relativeReportingPresets[preset as keyof typeof relativeReportingPresets] ?? formatDateRange(dateRange), showGrouping ? GROUPINGS.find(item => item.value === grouping)?.label : undefined, showChange ? CHANGES.find(item => item.value === changeDisplay)?.summary : undefined].filter((part): part is string => Boolean(part));
  const rangeChanged = draft.preset !== preset || draft.start !== dateRange.start || draft.end !== dateRange.end;
  // Apply writes the grouping the panel shows. Only a default range left to
  // regroup itself (no grouping chosen here) can omit it: applying it lets
  // grouping follow the range, which is what the panel previews.
  const groupingFollowsRange = draft.preset !== "custom" && !draft.groupingChosen;
  const groupingChanged = showGrouping && (rangeChanged ? !groupingFollowsRange : draft.grouping !== grouping);
  const change: DateChange = {
    ...(rangeChanged && {
      range: {
        preset: draft.preset,
        start: draft.start,
        end: draft.end
      }
    }),
    ...(groupingChanged && {
      grouping: draft.grouping
    }),
    ...(draft.changeDisplay !== changeDisplay && {
      changeDisplay: draft.changeDisplay
    })
  };
  function openChange(next: boolean) {
    // Opening starts from what the report shows; closing drops staged edits.
    if (next) setDraft(applied);
    setOpen(next);
  }
  function pickPreset(value: string) {
    if (value === draft.preset) return;
    const range = getDatePreset(value);
    setDraft(current => ({
      ...current,
      preset: value,
      ...range,
      // A new range regroups itself unless grouping was chosen here.
      grouping: current.groupingChosen ? current.grouping : autoGrouping(range.start, range.end)
    }));
  }
  return <ChipPanel ariaLabel={`Date range: ${summary.join(" · ")}`} panelLabel="Date range" icon={<CalendarDays size={14} />} label={summary} open={open} onOpenChange={openChange} dirty={Object.keys(change).length > 0} onApply={() => {
    onApply(change);
    setOpen(false);
  }}>
      <PanelColumn>
        <PanelListSection label="Default" name="Default" grid options={PRESETS} selected={[draft.preset]} onSelect={pickPreset} />
        <ChipPanelSection label="Custom range">
          <div className="x78zum5 x6s0dn4 xsuxsxl">
            {(["start", "end"] as const).map((field, index) => <span key={field} className="x78zum5 x6s0dn4 xsuxsxl">
                {index > 0 && <span aria-hidden className="xovanlf x7x6yu3">
                    –
                  </span>}
                <DatePicker label={field === "start" ? "Start date" : "End date"} value={draft[field]} max={maxSelectableDay} onChange={value => setDraft(current => ({
              ...current,
              preset: "custom",
              [field]: clampSelectableReportingDay(value)
            }))} />
              </span>)}
          </div>
        </ChipPanelSection>
        {showGrouping && <PanelSegments label="Group by" options={GROUPINGS} value={draft.grouping} onChange={value => setDraft(current => ({
        ...current,
        grouping: value,
        groupingChosen: true
      }))} />}
        {showChange && <PanelSegments label="Compare with previous period" options={CHANGES} value={draft.changeDisplay} onChange={value => setDraft(current => ({
        ...current,
        changeDisplay: value
      }))} />}
      </PanelColumn>
    </ChipPanel>;
}